import React from 'react';
import PropTypes from 'prop-types';
import { adopt } from 'react-adopt';
import gql from 'graphql-tag';
import { Query } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator/PaginatorContainer';

import { defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS } from 'config';
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import PaymentList from './List';

export const PAYMENTS_LIST = gql`
  query PaymentList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $id: ID
    $externalId: String
    $user: ID
    $agency: ID
    $status: [FilterPaymentStatus]
    $type: [FilterType]
    $startDateLte: DateTime
    $startDateGte: DateTime
    $orderType: [FilterOrderType]
  ) {
    payments(
      first: $first
      after: $after
      last: $last
      before: $before
      paymentId: $id
      platformPaymentId: $externalId
      user_ById: $user
      agency_ById: $agency
      status: $status
      type: $type
      createdOn_Lte: $startDateLte
      createdOn_Gte: $startDateGte
      orderType: $orderType
    ) {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          platformPaymentId
          createdOn
          updatedOn
          updatedOn
          amount
          currency
          status
          type
          user {
            email
            fullname
          }
          type
          order {
            orderType
            ... on AdminCreditOrderType {
              agency {
                id
                fullname
                email
                phone
              }
            }
          }
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Default filters
 */
export const defaultFilters = {
  status: undefined,
  startDateGte: undefined,
  startDateLte: undefined,
  id: undefined,
  externalId: undefined,
  user: undefined,
  agency: undefined,
  orderType: undefined,
};

class PaymentListContainer extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...restFilters
    } = props.initialFilters;
    this.state = {
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      pagination: {
        direction: direction || 'NEXT',
        cursor: cursor || undefined,
      },
      filters: {
        ...defaultFilters,
        ...restFilters,
      },
    };
    this.Compose = adopt({
      list: this.ListQuery,
    });
  }

  ListQuery = ({ render }) => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={PAYMENTS_LIST}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, error }) => render({ data, loading, error })}
      </Query>
    );
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;

    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const queryString = getQueryString(history);
    const urlString = qs.stringify({
      ...queryString,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;

    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const queryString = getQueryString(history);
    const urlString = qs.stringify({
      ...queryString,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters, ...filters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const urlString = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const urlString = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname: currentLocation, search: urlString });
  };

  render() {
    const { numberOfRowsSelected, pagination, filters } = this.state;

    return (
      <this.Compose>
        {({ list: { data, loading, error } }) => (
          <PaymentList
            data={data || { payments: {} }}
            error={error}
            loadingData={loading || !data}
            numberOfRowsSelected={numberOfRowsSelected}
            onChangePage={paginatorVariables => {
              this.handleChangePage(paginatorVariables);
            }}
            onChangeNumberOfRows={this.handleChangeNumberOfRows}
            paginationDirection={pagination.direction}
            appliedFilters={filters}
            onFilter={this.handleFilter}
            onRestartFilters={this.handleRestartFilters}
          />
        )}
      </this.Compose>
    );
  }
}

PaymentListContainer.propTypes = {
  initialFilters: PropTypes.instanceOf(Object).isRequired,
};

export default PaymentListContainer;
